import { http } from '@/shared/api';
import {
    cancelAllUpload,
    type OssUploadCredentials,
    uploadFile as uploadFileWithOss,
    uploadImage as uploadImageWithOss,
    uploadJson as uploadJsonWithOss,
} from '../lib/internal/oss-upload-adapter';
import type {
    UploadAsset,
    UploadOptions,
    UploadProgressCallback,
    UploadResult,
    UploadSource,
} from './upload-types';
import { nanoid } from 'nanoid';

export const configuredPublicHostOverride = (
    import.meta.env.VITE_OSS_PUBLIC_HOST_OVERRIDE?.trim()
    || ''
);

export function resolveOssPublicHostOverride(override = configuredPublicHostOverride) {
    const normalized = override.trim();
    if (!normalized) {
        return '';
    }

    try {
        return new URL(normalized).origin;
    } catch {
        return '';
    }
}

export function rewriteOssPublicUrlForDev(url: string, _override = configuredPublicHostOverride) {
    return url;
}

const TOKEN_EXPIRE_BUFFER_MS = 60_000;
const maxRetryUploader = 0;

let ossToken: OssUploadCredentials | null = null;

type OssTokenResponse = OssUploadCredentials | { oss_token?: OssUploadCredentials };

function resolveTokenPayload(data: OssTokenResponse | undefined): OssUploadCredentials | undefined {
    if (!data) {
        return undefined;
    }

    const maybeWrapper = data as { oss_token?: OssUploadCredentials };
    if (maybeWrapper.oss_token) {
        return maybeWrapper.oss_token;
    }

    return data as OssUploadCredentials;
}

function getFileExtension(source: UploadSource) {
    const rawName = typeof source === 'string' ? source : source.name;
    const dataUrlMimeMatch = typeof source === 'string'
        ? source.match(/^data:([^;,]+)/)
        : null;
    if (dataUrlMimeMatch?.[1]) {
        const mimeExtension = dataUrlMimeMatch[1].split('/').pop();
        return mimeExtension ? `.${mimeExtension}` : '';
    }

    const cleanName = typeof source === 'string'
        ? (rawName.split('?')[0]?.split('#')[0] ?? rawName)
        : rawName;
    const fileName = cleanName.split('/').pop() ?? '';
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex >= 0 ? fileName.slice(dotIndex) : '';
}

function normalizeSources(fileUri: UploadSource | UploadSource[]) {
    return Array.isArray(fileUri) ? fileUri : [fileUri];
}

function hasUsableOssToken(candidate: OssUploadCredentials | null) {
    const accessKeyId = candidate?.aliyun?.accessKeyId?.trim() || '';
    const accessKeySecret = candidate?.aliyun?.accessKeySecret?.trim() || '';
    const securityToken = candidate?.aliyun?.securityToken?.trim() || '';
    const domain = candidate?.domain?.trim() || '';
    const prefix = candidate?.prefix?.trim() || '';
    const endpoint = candidate?.endpoint?.trim() || '';
    const region = candidate?.region?.trim() || '';
    const expired = candidate?.expired;

    if (!accessKeyId || !accessKeySecret || !securityToken || !domain || !prefix || !endpoint || !region) {
        return false;
    }

    if (typeof expired === 'number' && expired * 1000 <= Date.now() + TOKEN_EXPIRE_BUFFER_MS) {
        return false;
    }

    return true;
}

async function resolveOssToken() {
    if (hasUsableOssToken(ossToken)) {
        return {
            code: 1,
            msg: 'ok',
            data: ossToken as OssUploadCredentials,
        };
    }

    const ossRes = await http.post<OssTokenResponse>('/zs/user/getOssToken', {});
    const nextToken = resolveTokenPayload(ossRes.data);
    if (ossRes.code !== 1 || !nextToken || !hasUsableOssToken(nextToken)) {
        return {
            code: ossRes.code || -1,
            msg: ossRes.msg || 'OSS 凭证获取失败',
            data: null,
        };
    }

    ossToken = nextToken;
    return {
        code: 1,
        msg: 'ok',
        data: nextToken,
    };
}

function createUploadAsset(source: UploadSource, url: string): UploadAsset {
    return {
        url: rewriteOssPublicUrlForDev(url),
        name: typeof source === 'string' ? undefined : source.name,
        size: typeof source === 'string' ? undefined : source.size,
        type: typeof source === 'string' ? undefined : source.type,
        source,
    };
}

function createSuccessResult(assets: UploadAsset[]): UploadResult {
    return {
        code: 1,
        msg: 'ok',
        data: {
            assets,
            urls: assets.map((asset) => asset.url),
        },
    };
}

function getFileSize(fileUri: UploadSource): number {
    if (typeof fileUri === 'string') {
        return 0;
    }

    return fileUri.size || 0;
}

function createBatchProgressCallback(
    sources: UploadSource[],
    progress?: UploadProgressCallback,
) {
    if (!progress) {
        return undefined;
    }

    const fileSizes = sources.map((fileObj) => getFileSize(fileObj));
    const totalSize = fileSizes.reduce((sum, size) => sum + size, 0);
    let uploadedSize = 0;
    let currentIndex = 0;
    let currentFileSize = 0;

    return {
        setCurrentIndex: (index: number) => {
            currentIndex = index;
            currentFileSize = fileSizes[index] ?? 0;
        },
        markCurrentUploaded: () => {
            uploadedSize += currentFileSize;
        },
        emit: (percent: number) => {
            const normalizedPercent = Math.max(0, Math.min(100, Math.floor(percent)));
            if (totalSize <= 0) {
                progress({
                    percent: normalizedPercent,
                    currentIndex,
                    totalCount: sources.length,
                });
                return;
            }

            const currentUploaded = uploadedSize + (currentFileSize * normalizedPercent / 100);
            const totalProgress = Math.max(0, Math.min(100, Math.floor((currentUploaded / totalSize) * 100)));
            progress({
                percent: totalProgress,
                loaded: currentUploaded,
                total: totalSize,
                currentIndex,
                totalCount: sources.length,
            });
        },
    };
}

function getFileKey(fileUri: UploadSource) {
    const randomName = nanoid(8);
    const ext = getFileExtension(fileUri);
    return `${randomName}${ext}`
}

async function uploadFile(fileUri: UploadSource | UploadSource[], options: UploadOptions = {}): Promise<UploadResult> {
    return uploadFiles(normalizeSources(fileUri), options);
}

async function uploadFiles(fileList: UploadSource[], options: UploadOptions = {}): Promise<UploadResult> {
    const ossTokenRes = await resolveOssToken();
    if (ossTokenRes.code !== 1 || !ossTokenRes.data) {
        return { code: ossTokenRes.code, msg: ossTokenRes.msg };
    }

    const assets: UploadAsset[] = [];
    const batchProgress = createBatchProgressCallback(fileList, options.progress);
    for (let i = 0; i < fileList.length; i++) {
        const fileObj = fileList[i];
        const fileKey = getFileKey(fileObj);
        batchProgress?.setCurrentIndex(i);

        const uploadRes = await uploadFileWithOss(ossTokenRes.data, fileObj, fileKey, 0, options.maxRetry ?? maxRetryUploader, batchProgress?.emit, options.abortSignal);
        if (uploadRes.code !== 1 || !uploadRes.data) {
            return { code: uploadRes.code, msg: uploadRes.msg };
        }

        assets.push(createUploadAsset(fileObj, uploadRes.data.url));
        batchProgress?.markCurrentUploaded();
    }

    return createSuccessResult(assets);
}

async function uploadImage(imageUri: UploadSource, options: UploadOptions = {}): Promise<UploadResult> {
    const ossTokenRes = await resolveOssToken();
    if (ossTokenRes.code !== 1 || !ossTokenRes.data) {
        return { code: ossTokenRes.code, msg: ossTokenRes.msg };
    }

    const uploadRes = await uploadImageWithOss(ossTokenRes.data, imageUri, getFileKey(imageUri), 0, options.maxRetry ?? maxRetryUploader, (percent) => {
        options.progress?.({ percent });
    }, options.abortSignal);
    if (uploadRes.code !== 1 || !uploadRes.data) {
        return { code: uploadRes.code, msg: uploadRes.msg };
    }

    return createSuccessResult([createUploadAsset(imageUri, uploadRes.data.url)]);
}

async function uploadJson(jsonText: string, options: UploadOptions = {}): Promise<UploadResult> {
    const ossTokenRes = await resolveOssToken();
    if (ossTokenRes.code !== 1 || !ossTokenRes.data) {
        return { code: ossTokenRes.code, msg: ossTokenRes.msg };
    }

    const uploadRes = await uploadJsonWithOss(ossTokenRes.data, jsonText, getFileKey('data.json'), 0, options.maxRetry ?? maxRetryUploader, (percent) => {
        options.progress?.({ percent });
    }, options.abortSignal);
    if (uploadRes.code !== 1 || !uploadRes.data) {
        return { code: uploadRes.code, msg: uploadRes.msg };
    }

    return createSuccessResult([createUploadAsset('data.json', uploadRes.data.url)]);
}

export const uploadService = {
    uploadFile,
    uploadFiles,
    uploadImage,
    uploadJson,
    cancelAll: cancelAllUpload,
};
